import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress, Tooltip, useMediaQuery } from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { Table, Select, message } from 'antd';

import { responsiveSpacing } from '../../themes/responsive';
import { useAuth } from '../../context/AuthContext';
import { fetchMasterProductsList, submitProductProposal } from '../../data/productsApi';

const defaultOptionSets = {
  categories: [],
  subcategories: [],
  subcategoriesByCategory: {},
  laneTypes: [],
};

const getPendingValue = (record, field) => (record.pendingProposal ? record.pendingProposal[field] : undefined);
const getCurrentValue = (record, field) => record[field];
const hasPendingOverride = (record, field) => {
  if (!record.pendingProposal) return false;
  if (!Object.prototype.hasOwnProperty.call(record.pendingProposal, field)) return false;
  const value = record.pendingProposal[field];
  if (value === undefined) return false;
  if (value === null) {
    // We only want to honor a null override for subcategory, which intentionally clears after a category change
    return field === 'subcategory';
  }
  return true;
};
const getDisplayedValue = (record, field) => (
  hasPendingOverride(record, field)
    ? getPendingValue(record, field)
    : getCurrentValue(record, field)
);

export const SettingsProductsTab = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const [productsData, setProductsData] = React.useState([]);
  const [optionSets, setOptionSets] = React.useState(defaultOptionSets);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [submittingCells, setSubmittingCells] = React.useState({});

  const refreshProducts = React.useCallback(async () => {
    if (!currentUser?.customer_id) return;
    const response = await fetchMasterProductsList(currentUser.customer_id);
    setProductsData(response.products || []);
    setOptionSets({
      categories: response.options?.categories || [],
      subcategories: response.options?.subcategories || [],
      subcategoriesByCategory: response.options?.subcategoriesByCategory || {},
      laneTypes: response.options?.laneTypes || [],
    });
  }, [currentUser]);

  const loadProducts = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await refreshProducts();
    } catch (err) {
      console.error('Error loading products data:', err);
      setError(t('loadProductsError'));
    } finally {
      setLoading(false);
    }
  }, [refreshProducts, t]);

  React.useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const updateLocalPending = React.useCallback((productId, field, value) => {
    const editableFields = ['productCategory', 'subcategory', 'laneType'];

    setProductsData((prev) => prev.map((product) => {
      if (product.id !== productId) return product;

      const updatedProduct = { ...product };
      const existingPending = product.pendingProposal ? { ...product.pendingProposal } : {};
      const pendingValues = { ...existingPending };

      delete pendingValues.proposedBy;
      delete pendingValues.updatedAt;
      delete pendingValues.id;


      if (field === 'productCategory') {
        updatedProduct.productCategory = value ?? null;
      } else if (field === 'subcategory') {
        updatedProduct.subcategory = value ?? null;
      } else if (field === 'laneType') {
        updatedProduct.laneType = value ?? null;
      }


      if (value === undefined || value === '') {
        delete pendingValues[field];
      } else {
        pendingValues[field] = value;
      }

      const hasTrackedFields = editableFields.some((editableField) => (
        Object.prototype.hasOwnProperty.call(pendingValues, editableField)
      ));

      if (!hasTrackedFields) {
        return {
          ...updatedProduct,
          hasPendingProposal: false,
          pendingProposal: null,
        };
      }

      const nextPending = {
        ...pendingValues,
        proposedBy: existingPending.proposedBy || currentUser?.email || null,
        updatedAt: new Date().toISOString(),
      };

      if (existingPending.id) {
        nextPending.id = existingPending.id;
      }

      return {
        ...updatedProduct,
        hasPendingProposal: true,
        pendingProposal: nextPending,
      };
    }));
  }, [currentUser]);

  const handleCellChange = React.useCallback(async (record, field, value) => {
    if (!currentUser?.customer_id) {
      message.error(t('proposalError'));
      return;
    }

    const currentValue = getDisplayedValue(record, field);
    if (value === currentValue) {
      return;
    }

    const key = `${record.id}:${field}`;
    const payload = {};
    const pendingProposal = record.pendingProposal || {};

    if (field !== 'productCategory' && pendingProposal.productCategory !== undefined && pendingProposal.productCategory !== null) {
      payload.productCategory = pendingProposal.productCategory;
    }
    if (field !== 'subcategory' && pendingProposal.subcategory !== undefined) {
      payload.subcategory = pendingProposal.subcategory;
    }
    if (field !== 'laneType' && pendingProposal.laneType !== undefined && pendingProposal.laneType !== null) {
      payload.laneType = pendingProposal.laneType;
    }

    payload[field] = value;

    const payloadPreview = field === 'productCategory'
      ? { ...payload, subcategory: null }
      : { ...payload };

    setSubmittingCells((prev) => ({ ...prev, [key]: true }));
    updateLocalPending(record.id, field, value ?? null);
    if (field === 'productCategory') {
      updateLocalPending(record.id, 'subcategory', null);
      payloadPreview.subcategory = null;
    } else if (field === 'subcategory') {
      const currentCategory = getDisplayedValue(record, 'productCategory');
      if (currentCategory !== undefined && currentCategory !== null) {
        payloadPreview.productCategory = currentCategory;
      }
    } else if (field === 'laneType') {
      const currentCategory = getDisplayedValue(record, 'productCategory');
      const currentSubcategory = getDisplayedValue(record, 'subcategory');
      if (currentCategory !== undefined && currentCategory !== null) {
        payloadPreview.productCategory = currentCategory;
      }
      if (currentSubcategory !== undefined) {
        payloadPreview.subcategory = currentSubcategory;
      }
    }

    try {
      await submitProductProposal(currentUser.customer_id, record.id, payloadPreview);
      message.success(t('proposalSuccess'));
      await refreshProducts();
    } catch (submitError) {
      const apiMessage = submitError?.response?.data?.message;
      message.error(apiMessage || t('proposalError'));
      await refreshProducts();
    } finally {
      setSubmittingCells((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [currentUser, updateLocalPending, refreshProducts, t]);

  const getSubcategoryOptions = React.useCallback((record) => {
    const category = getDisplayedValue(record, 'productCategory');
    if (!category) {
      return optionSets.subcategories;
    }
    const byCategory = optionSets.subcategoriesByCategory || {};
    return byCategory[category] || [];
  }, [optionSets]);

  const renderDropdownCell = React.useCallback((field, optionsInput, placeholderKey) => {
    return (_value, record) => {
      const pendingValue = getPendingValue(record, field);
      const currentValue = getCurrentValue(record, field);
      const pendingOverride = hasPendingOverride(record, field);
      const displayedValue = getDisplayedValue(record, field);
      const key = `${record.id}:${field}`;
      const isSubmitting = submittingCells[key];
      const showPending = pendingOverride && pendingValue !== currentValue;

      const options = typeof optionsInput === 'function' ? optionsInput(record) : optionsInput;
      let normalizedOptions = (Array.isArray(options) ? options : []).filter((option) => option !== null && option !== undefined && `${option}` !== '');

      const hasDisplayedValue = displayedValue !== undefined && displayedValue !== null && `${displayedValue}` !== '';
      if (hasDisplayedValue && !normalizedOptions.includes(displayedValue)) {
        normalizedOptions = [...normalizedOptions, displayedValue];
      }

      const safeValue = normalizedOptions.includes(displayedValue)
        ? displayedValue
        : undefined;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Select
            size="small"
            style={{ minWidth: 120 }}
            value={safeValue ?? undefined}
            onChange={(value) => handleCellChange(record, field, value)}
            options={normalizedOptions.map((option) => ({ label: option, value: option }))}
            placeholder={t(placeholderKey)}
            disabled={isSubmitting || normalizedOptions.length === 0}
          />
          {showPending && (
            <Tooltip title={t('proposalPendingTooltip', { value: pendingValue })} placement="top">
              <NotificationsActive fontSize="small" color="warning" />
            </Tooltip>
          )}
        </Box>
      );
    };
  }, [handleCellChange, submittingCells, t]);

  const columns = React.useMemo(() => ([
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      ellipsis: true,
    },
    {
      title: t('category'),
      dataIndex: 'productCategory',
      key: 'productCategory',
      sorter: (a, b) => (a.productCategory || '').localeCompare(b.productCategory || ''),
      render: renderDropdownCell('productCategory', optionSets.categories, 'selectNewCategory'),
    },
    {
      title: t('subcategory'),
      dataIndex: 'subcategory',
      key: 'subcategory',
      sorter: (a, b) => (a.subcategory || '').localeCompare(b.subcategory || ''),
      render: renderDropdownCell('subcategory', getSubcategoryOptions, 'selectNewSubcategory'),
    },
    {
      title: t('laneType'),
      dataIndex: 'laneType',
      key: 'laneType',
      sorter: (a, b) => (a.laneType || '').localeCompare(b.laneType || ''),
      render: renderDropdownCell('laneType', optionSets.laneTypes, 'selectNewLaneType'),
    },
  ]), [t, optionSets, renderDropdownCell, getSubcategoryOptions]);

  if (error) {
    return (
      <Box sx={{ ...responsiveSpacing.pagePadding }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('masterProducts')}
        </Typography>
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...responsiveSpacing.pagePadding }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('masterProducts')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('completeProductsList')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table
          columns={columns}
          dataSource={productsData}
          rowKey="id"
          pagination={{
            pageSize: 50,
            simple: isSmallScreen,
            showSizeChanger: !isSmallScreen,
            showQuickJumper: !isSmallScreen,
            showTotal: (total, range) => t('paginationTotal', {
              start: range[0],
              end: range[1],
              total,
            }),
          }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      )}
    </Box>
  );
};
